import { faKeySkeleton, faXmark } from '@awesome.me/kit-a1e2571845/icons/classic/regular'
import { css } from '@emotion/css'
import { zodResolver } from '@hookform/resolvers/zod'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ErrorBoundary } from '@sentry/react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { DataGrid, Icon } from '@xweather/theme'
import { enqueueSnackbar } from 'notistack'
import { type ComponentProps, Suspense, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { I18n } from '../../components/I18n.tsx'
import { IconButton } from '../../components/IconButton.tsx'
import { WithoutNavigation } from '../../components/Layouts.tsx'
import { useConfirmDialog } from '../../components/useConfirmDialog.tsx'
import { apiKeyData, type InsightUser, roleData, useCreateUser, useDeleteUser, userData, useUpdateUser } from '../../data/users.ts'
import { dayjs } from '../../dayjs.ts'
import { useFormatDate } from '../../features/preferences/useFormatDate.tsx'

export const Route = createFileRoute('/_app/api-keys')({
	beforeLoad: ({ context: { isAdmin, sub, impersonation } }) => {
		if (!isAdmin || (!sub.features.includes('devices') && !impersonation)) throw redirect({ to: '/' })
	},
	loader: async ({ context }) => {
		const [users] = await Promise.all([userData.ensure(context), roleData.ensure()])
		users
			.filter((user) => user.isServiceUser)
			.map((user) => user.id)
			.forEach(apiKeyData.prefetch)
	},
	component: ApiKeys,
})

function ApiKeys() {
	const { data: users } = userData.use()
	const navigate = Route.useNavigate()
	const [editingId, setEditingId] = useState<string>()
	const { DeleteDialog, askToDelete } = useDelete()

	const apiKeys: ApiKey[] = users.filter((user) => user.isServiceUser).toSorted((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt)))
	if (editingId === NEW_API_KEY_ID) apiKeys.unshift({ id: NEW_API_KEY_ID, fullName: '', isNew: true })

	return (
		<WithoutNavigation
			onBack={() => navigate({ to: '..' })}
			topBar={
				<Stack full spacing={2} sx={{ align: 'center' }}>
					<Icon icon={faKeySkeleton} />
					<Typography variant='h1'>
						<I18n i18nKey={($) => $.navigation.apiKeys} />
					</Typography>
					<Box sx={{ flexGrow: 1 }} />
					<Button onClick={() => setEditingId(NEW_API_KEY_ID)}>
						<I18n i18nKey={($) => $.apiKeys.create} />
					</Button>
				</Stack>
			}
		>
			<DataGrid
				data={apiKeys}
				columns={[
					{
						accessorKey: 'fullName',
						header: () => <I18n i18nKey={($) => $.apiKeys.name} />,
						size: 400,
						cell: ({ row }) => <NameCell key={row.id} user={row.original} editingId={editingId} setEditingId={(id) => setEditingId(id)} />,
					},
					{
						id: 'key',
						header: () => <I18n i18nKey={($) => $.apiKeys.apiKey} />,
						size: 250,
						meta: { grow: 1 },
						cell: ({ row }) =>
							!row.original.isNew && (
								<Stack full sx={{ align: 'center' }}>
									<ErrorBoundary fallback={<Icon icon={faXmark} color='error' />}>
										<Suspense fallback={<CircularProgress />}>
											<KeyCell apiKey={row.original} onDelete={() => askToDelete(row.original)} />
										</Suspense>
									</ErrorBoundary>
								</Stack>
							),
					},
					{
						accessorKey: 'isActive',
						header: () => <I18n i18nKey={($) => $.apiKeys.status} />,
						cell: ({ row }) => !row.original.isNew && <StatusCell apiKey={row.original} />,
					},
					{
						accessorKey: 'createdAt',
						header: () => <I18n i18nKey={($) => $.apiKeys.created} />,
						cell: ({ row }) => !row.original.isNew && <CreatedAtCell apiKey={row.original} />,
					},
				]}
				slotProps={{
					tableContainer: { className: 'no-zoom' },
					bodyRow: { sx: { ':hover': { [`.${showOnHover}`]: { visibility: 'visible' } } } },
				}}
			/>

			{DeleteDialog}
		</WithoutNavigation>
	)
}

const NEW_API_KEY_ID = 'new-api-key-id'
interface NewApiKey extends Partial<InsightUser> {
	id: typeof NEW_API_KEY_ID
	fullName: string
	isNew: true
}
type ApiKey = (InsightUser & { isNew?: false }) | NewApiKey

const showOnHover = css({ visibility: 'hidden' })

const ActionButton = (props: ComponentProps<typeof IconButton>) => (
	<IconButton className={showOnHover} variant='text' size='small' color='primary' {...props} />
)

const NameCell = ({ user, editingId, setEditingId }: { user: ApiKey; editingId?: string; setEditingId: (id?: string) => void }) => {
	const { t } = useTranslation()
	const { data: roles } = roleData.use()
	const apiKeySchema = z.object({ name: z.string().min(2) })
	const form = useForm({ defaultValues: { name: user.fullName }, resolver: zodResolver(apiKeySchema) })

	const { mutate: createUser, isPending: createInProgress } = useCreateUser()
	const { mutate: updateUser, isPending: updateInProgress } = useUpdateUser()
	const createApiKeyUser = async (name: string) =>
		createUser(
			{ email: '', name, blocked: false, roles: [roles.user?.id ?? ''], type: 'Individual', subType: 'ServiceUser' },
			{
				onSuccess: async () => {
					setEditingId(undefined)
					enqueueSnackbar(<I18n i18nKey={($) => $.apiKeys.createApiKeySuccess} />, { variant: 'success' })
				},
				onError: () => enqueueSnackbar(<I18n i18nKey={($) => $.apiKeys.createApiKeyFailure} />, { variant: 'error' }),
			},
		)
	const updateApiKeyUser = async (userId: string, name: string) =>
		updateUser(
			{ id: userId, body: { name } },
			{
				onSuccess: async () => {
					setEditingId(undefined)
					enqueueSnackbar(<I18n i18nKey={($) => $.apiKeys.updateApiKeySuccess} />, { variant: 'success' })
				},
				onError: () => enqueueSnackbar(<I18n i18nKey={($) => $.apiKeys.updateApiKeyFailure} />, { variant: 'error' }),
			},
		)

	const onSave = async ({ name }: z.infer<typeof apiKeySchema>) => {
		if (user.isNew) await createApiKeyUser(name)
		else await updateApiKeyUser(user.id, name)
	}

	const savingDisabled = !form.formState.isDirty || !form.formState.isValid
	const isSaving = updateInProgress || createInProgress

	if (editingId !== user.id)
		return (
			<Stack full center sx={{ justify: 'space-between' }}>
				<Typography>{user.fullName}</Typography>
				<ActionButton
					icon='PenLine'
					label={t(($) => $.edit)}
					onClick={() => {
						form.reset()
						setEditingId(user.id)
					}}
				/>
			</Stack>
		)

	return (
		<Stack sx={{ align: 'center' }}>
			<FormProvider {...form}>
				<TextField
					required
					autoFocus
					size='small'
					placeholder={t(($) => $.apiKeys.enterNewApiKeyName)}
					value={form.watch('name')}
					onChange={(e) => form.setValue('name', e.target.value, { shouldDirty: true, shouldValidate: true })}
				/>
				{isSaving ? (
					<CircularProgress size={16} />
				) : (
					<IconButton
						icon='Check'
						label={t(($) => $.save)}
						color='primary'
						variant='text'
						size='small'
						disabled={savingDisabled}
						onClick={() => onSave(form.getValues())}
					/>
				)}
				<IconButton icon='Xmark' label={t(($) => $.cancel)} color='error' size='small' onClick={() => setEditingId(undefined)} />
			</FormProvider>
		</Stack>
	)
}

const KeyCell = ({ apiKey, onDelete }: { apiKey: ApiKey; onDelete: () => void }) => {
	const { t } = useTranslation()
	const { data } = apiKeyData.use(apiKey.id)
	const key = `${data.id}.${data.key}`
	const { mutate: updateUser, isPending } = useUpdateUser()
	return (
		<Stack full center sx={{ justifyContent: 'space-between' }}>
			<Typography sx={{ flexShrink: 1, textDecoration: apiKey.isBlocked ? 'line-through' : 'none' }} noWrap>
				{key}
			</Typography>
			<Stack center>
				<ActionButton
					icon='Copy'
					label={t(($) => $.copy)}
					onClick={async () => {
						await navigator.clipboard.writeText(key)
						enqueueSnackbar(<I18n i18nKey={($) => $.apiKeys.copyApiKeySuccess} />, { variant: 'success', preventDuplicate: false })
					}}
				/>
				{isPending && (
					<Stack center sx={{ width: 32, height: 32 }}>
						<CircularProgress size={18} />
					</Stack>
				)}
				{!isPending && apiKey.isActive && (
					<ActionButton
						icon='Ban'
						label={t(($) => $.apiKeys.block)}
						color='error'
						onClick={() => updateUser({ id: apiKey.id, body: { blocked: true } })}
					/>
				)}
				{!isPending && apiKey.isBlocked && (
					<ActionButton
						icon='Check'
						label={t(($) => $.apiKeys.unblock)}
						onClick={() => updateUser({ id: apiKey.id, body: { blocked: false } })}
					/>
				)}
				<ActionButton icon='Trash' label={t(($) => $.delete)} color='error' onClick={() => onDelete()} />
			</Stack>
		</Stack>
	)
}

const StatusCell = ({ apiKey }: { apiKey: ApiKey }) => {
	return (
		<Stack full sx={{ align: 'center' }}>
			{apiKey.isActive && <Chip color='success' label={<I18n i18nKey={($) => $.users.active} />} />}
			{!apiKey.isActive && <Chip color='error' label={<I18n i18nKey={($) => $.users.blocked} />} />}
		</Stack>
	)
}

const CreatedAtCell = ({ apiKey }: { apiKey: ApiKey }) => {
	const formatDate = useFormatDate()
	return (
		<Stack full sx={{ align: 'center' }}>
			<Typography>{apiKey.createdAt ? formatDate(apiKey.createdAt, 'DD.MM.YYYY') : '-'}</Typography>
		</Stack>
	)
}

const useDelete = () => {
	const [deleting, setDeleting] = useState<ApiKey>()
	const { mutateAsync: deleteUser } = useDeleteUser()
	const { Dialog, openDialog } = useConfirmDialog({
		title: <I18n i18nKey={($) => $.delete} />,
		content: <I18n i18nKey={($) => $.apiKeys.deleteApiKeyConfirmation} values={{ name: deleting?.fullName }} />,
		actionName: <I18n i18nKey={($) => $.delete} />,
		color: 'error',
		onConfirm: async () => {
			if (deleting)
				await deleteUser(deleting.id, {
					onSuccess: async () => {
						enqueueSnackbar(<I18n i18nKey={($) => $.apiKeys.deleteApiKeySuccess} />, { variant: 'success' })
					},
					onError: () => enqueueSnackbar(<I18n i18nKey={($) => $.apiKeys.deleteApiKeyFailure} />, { variant: 'error' }),
				})
			setDeleting(undefined)
		},
	})

	return {
		DeleteDialog: Dialog,
		askToDelete: (apiKey: ApiKey) => {
			setDeleting(apiKey)
			openDialog()
		},
	}
}
